import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { initAnalytics, stopAnalytics } from "../lib/analytics";

const PREFS_KEY = "ttl-prefs";
const CONSENT_KEY = "ttl-consent";

const DEFAULT_PREFS = {
  voice: "male",        // "male" (Thriver) | "female" (Life) — names hidden in UI
  mode: "read",         // "read" | "talk" | "listen"
  chatLang: "en",       // Thriver reply language — decoupled from site UI language
  playbackSpeed: 1,
  fontSize: "base",     // "sm" | "base" | "lg"
  soundOn: false,
  lastPage: null,
  savedArticles: [],
};

// Map any legacy stored preferences to the current voice ids (male/female).
function migratePrefs(p) {
  const voiceMigration = { thriver: "male", aurora: "female" };
  if (p && voiceMigration[p.voice]) p.voice = voiceMigration[p.voice];
  return p;
}

const PreferencesContext = createContext(null);

export function PreferencesProvider({ children }) {
  const location = useLocation();
  // consent: null (undecided) | "granted" | "declined"
  const [consent, setConsent] = useState(() => localStorage.getItem(CONSENT_KEY));
  const [bannerOpen, setBannerOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => {
    if (localStorage.getItem(CONSENT_KEY) === "granted") {
      try {
        return migratePrefs({ ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || "{}") });
      } catch (e) {
        return { ...DEFAULT_PREFS };
      }
    }
    return { ...DEFAULT_PREFS };
  });

  // Persist only when explicit consent is granted.
  useEffect(() => {
    if (consent === "granted") {
      localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    }
  }, [prefs, consent]);

  // Remember last page visited (with consent).
  useEffect(() => {
    if (consent === "granted") {
      setPrefs((p) => ({ ...p, lastPage: location.pathname }));
    }
  }, [location.pathname, consent]);

  const setPref = useCallback((key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
  }, []);

  const toggleSavedArticle = useCallback((id) => {
    setPrefs((p) => {
      const saved = p.savedArticles.includes(id)
        ? p.savedArticles.filter((a) => a !== id)
        : [...p.savedArticles, id];
      return { ...p, savedArticles: saved };
    });
  }, []);

  // Analytics is gated on consent. On mount, honour the returning visitor's
  // choice: if they previously granted, initialise PostHog now; if they
  // previously declined, ensure any residual analytics state is torn down.
  useEffect(() => {
    if (consent === "granted") {
      initAnalytics();
    } else if (consent === "declined") {
      stopAnalytics();
    }
    // consent === null (undecided) → do nothing. PostHog is not injected.
  }, [consent]);

  const grantConsent = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "granted");
    setConsent("granted");
    setBannerOpen(false);
    // Fire the analytics init here as well as in the effect, so the first
    // capture happens without waiting for the render cycle.
    initAnalytics();
  }, []);

  const declineConsent = useCallback(() => {
    localStorage.setItem(CONSENT_KEY, "declined");
    localStorage.removeItem(PREFS_KEY);
    setConsent("declined");
    setBannerOpen(false);
    stopAnalytics();
  }, []);

  const openPrivacy = useCallback(() => setBannerOpen(true), []);

  const forgetMe = useCallback(() => {
    localStorage.removeItem(PREFS_KEY);
    localStorage.removeItem(CONSENT_KEY);
    setPrefs({ ...DEFAULT_PREFS });
    setConsent(null);
    setBannerOpen(false);
    stopAnalytics();
  }, []);

  return (
    <PreferencesContext.Provider
      value={{ consent, prefs, setPref, toggleSavedArticle, grantConsent, declineConsent, openPrivacy, bannerOpen, forgetMe }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error("usePreferences must be used within PreferencesProvider");
  return ctx;
}
